import {
  axesToThemeV2,
  CANONICAL_THEME,
  resolveTheme,
  type DesignSystemAxes,
} from '@cogo/design-presets'
import {
  AXIS_KEYS,
  BASE_COLOR_OPTIONS,
  CHART_OPTIONS,
  FONT_OPTIONS,
  RADIUS_OPTIONS,
  STYLE_OPTIONS,
  THEME_OPTIONS,
  type LabeledOption,
} from '../lib/design-system-options'

export type DecisionScope = 'theme' | 'component'
export type DecisionProvenance = 'project' | 'default' | 'overridden'
export type AxisName = (typeof AXIS_KEYS)[number]

export interface DesignDecision {
  /** `--primary`, or `button.defaultVariant` — the value's own name. */
  key: string
  scope: DecisionScope
  /** The component this value dresses, or null for a theme-wide value. */
  component: string | null
  value: string
  provenance: DecisionProvenance
  /** The editor control that decides this value; null = nothing here reaches it. */
  controlledBy: AxisName | null
  /**
   * Set when the creator HAS a control for this value and another choice has
   * taken it away. Naming the thief is the point: "default" would imply nobody
   * decided, which is false.
   */
  overriddenBy: AxisName | null
}

export interface ScopeSummary {
  theme: {
    /** How many theme-wide values the paint path resolves. */
    values: number
  }
  component: {
    /** Components this screen can actually reach, from the resolved theme. */
    reachableHere: string[]
    /**
     * Components the platform's canonical theme declares — read from that
     * file, not written here, so the gap between the two is always the real
     * one.
     */
    declaredBySystem: number
  }
}

export interface AxisEffect {
  /** How many resolved values this control decides right now. */
  decides: number
  /**
   * Set when the control decides nothing here because another choice took it.
   * Distinct from `decides: 0` alone — a control can be inert with no thief.
   */
  overriddenBy: AxisName | null
}

/**
 * The creator's actual choices, taken from the lists the editor renders — not
 * from the raw presets. If a control stops offering an option, this narrows
 * with it.
 */
const AXIS_OPTIONS: Record<AxisName, readonly LabeledOption[]> = {
  style: STYLE_OPTIONS,
  baseColor: BASE_COLOR_OPTIONS,
  theme: THEME_OPTIONS,
  chart: CHART_OPTIONS,
  radius: RADIUS_OPTIONS,
  font: FONT_OPTIONS,
}

/** Every value the paint path actually produces, theme-wide and per component. */
function flattenResolved(axes: DesignSystemAxes): Record<string, string> {
  const resolved = resolveTheme(axesToThemeV2(axes))
  const out: Record<string, string> = { ...resolved.cssVars }
  for (const [atom, props] of Object.entries(resolved.atomProps)) {
    for (const [prop, val] of Object.entries(props as Record<string, unknown>)) {
      out[`${atom}.${prop}`] = String(val)
    }
  }
  return out
}

/**
 * Which control decides which value — MEASURED, by moving each control through
 * its own options and watching what the producer does, rather than asserted by
 * a hand-written table. A table would go stale the day an axis stops feeding a
 * token and nothing would say so; this cannot, because it *is* the producer's
 * behaviour.
 *
 * `axesToThemeV2` and `resolveTheme` are pure, so this is a closed computation
 * over roughly (options per axis) resolutions — small, and done once per call.
 */
function measureMovers(axes: DesignSystemAxes): Map<string, Set<AxisName>> {
  const base = flattenResolved(axes)
  const movers = new Map<string, Set<AxisName>>()

  for (const axis of AXIS_KEYS) {
    for (const option of AXIS_OPTIONS[axis]) {
      if (option.value === axes[axis]) continue
      const moved = flattenResolved({ ...axes, [axis]: option.value })
      for (const key of Object.keys(base)) {
        if (moved[key] === base[key]) continue
        let set = movers.get(key)
        if (!set) movers.set(key, (set = new Set()))
        set.add(axis)
      }
    }
  }
  return movers
}

/**
 * Of the controls that move a value, the one that moves the FEWEST values
 * overall — the most specific control, which is the one a creator would think
 * of as "the" control for it.
 *
 * Without this, attribution collapses: `style` moves nearly every token, so
 * picking by axis order would label almost everything "style" and the column
 * would carry no information.
 */
function narrowestMover(
  candidates: Set<AxisName>,
  breadth: Map<AxisName, number>,
): AxisName {
  let best: AxisName | null = null
  for (const axis of candidates) {
    if (best === null || (breadth.get(axis) ?? 0) < (breadth.get(best) ?? 0)) best = axis
  }
  return best as AxisName
}

/**
 * Values a control moves SOMEWHERE, for a control that moves nothing here.
 *
 * Measured, not declared: vary the other controls one at a time until this one
 * comes alive, then record what it touches. Paid only for a control that is
 * currently inert, which is normally none of them.
 */
function claimsOfInertAxis(
  axis: AxisName,
  axes: DesignSystemAxes,
): Set<string> {
  for (const other of AXIS_KEYS) {
    if (other === axis) continue
    for (const option of AXIS_OPTIONS[other]) {
      if (option.value === axes[other]) continue
      const alt = { ...axes, [other]: option.value }
      const altBase = flattenResolved(alt)
      const claimed = new Set<string>()
      for (const own of AXIS_OPTIONS[axis]) {
        if (own.value === alt[axis]) continue
        const moved = flattenResolved({ ...alt, [axis]: own.value })
        for (const key of Object.keys(altBase)) {
          if (moved[key] !== altBase[key]) claimed.add(key)
        }
      }
      if (claimed.size > 0) return claimed
    }
  }
  return new Set()
}

/**
 * Every design value in effect, each carrying who decided it and what it
 * covers.
 *
 * `provenance` is 'project' only when the creator moved the control that
 * decides the value away from the baseline. A value that no control reaches
 * (`controlledBy: null`) is always 'default' — the page must not imply the
 * creator chose something they cannot reach.
 */
interface Analysis {
  decisions: DesignDecision[]
  axisEffect: Record<AxisName, AxisEffect>
}

function analyze(axes: DesignSystemAxes, baseline: DesignSystemAxes): Analysis {
  const resolved = resolveTheme(axesToThemeV2(axes))
  const movers = measureMovers(axes)

  const breadth = new Map<AxisName, number>()
  for (const set of movers.values()) {
    for (const axis of set) breadth.set(axis, (breadth.get(axis) ?? 0) + 1)
  }

  // A control that moves nothing here, but moves something somewhere, has been
  // taken over by another choice. Record both what it would have owned and who
  // took it, so the axis list can name the thief rather than showing a value
  // that is not in effect.
  const stolen = new Map<string, AxisName>()
  const stolenFrom = new Map<AxisName, AxisName>()
  for (const axis of AXIS_KEYS) {
    if ((breadth.get(axis) ?? 0) > 0) continue
    for (const key of claimsOfInertAxis(axis, axes)) {
      const thieves = movers.get(key)
      if (!thieves || thieves.size === 0) continue
      const thief = narrowestMover(thieves, breadth)
      stolen.set(key, thief)
      if (!stolenFrom.has(axis)) stolenFrom.set(axis, thief)
    }
  }

  const decide = (
    key: string,
    scope: DecisionScope,
    component: string | null,
    value: string,
  ): DesignDecision => {
    const candidates = movers.get(key)
    const controlledBy =
      candidates && candidates.size > 0 ? narrowestMover(candidates, breadth) : null
    const overriddenBy = stolen.get(key) ?? null

    let provenance: DecisionProvenance = 'default'
    if (overriddenBy !== null) provenance = 'overridden'
    else if (controlledBy !== null && axes[controlledBy] !== baseline[controlledBy])
      provenance = 'project'

    return { key, scope, component, value, provenance, controlledBy, overriddenBy }
  }

  const decisions: DesignDecision[] = []
  for (const [key, value] of Object.entries(resolved.cssVars)) {
    decisions.push(decide(key, 'theme', null, value))
  }
  for (const [atom, props] of Object.entries(resolved.atomProps)) {
    const entries = Object.entries(props as Record<string, unknown>)
    if (entries.length === 0) {
      // The platform declares this component with nothing to decide (`lottie`,
      // `aspect-ratio`). Emitting nothing made it indistinguishable from a
      // component that does not exist, and made the count on screen disagree
      // with the theme by two.
      decisions.push(decide(atom, 'component', atom, ''))
      continue
    }
    for (const [prop, val] of entries) {
      decisions.push(decide(`${atom}.${prop}`, 'component', atom, String(val)))
    }
  }

  const axisEffect = {} as Record<AxisName, AxisEffect>
  for (const axis of AXIS_KEYS) {
    axisEffect[axis] = {
      decides: decisions.filter((d) => d.controlledBy === axis).length,
      overriddenBy: stolenFrom.get(axis) ?? null,
    }
  }

  return { decisions, axisEffect }
}

/**
 * Every design value in effect, each carrying who decided it and what it
 * covers.
 *
 * `provenance` is 'project' only when the creator moved the control that
 * decides the value away from the baseline. A value that no control reaches
 * (`controlledBy: null`) is always 'default' — the page must not imply the
 * creator chose something they cannot reach.
 */
export function describeDesignDecisions(
  axes: DesignSystemAxes,
  baseline: DesignSystemAxes,
): DesignDecision[] {
  return analyze(axes, baseline).decisions
}

/**
 * Per control: whether the creator's choice is actually in effect, and if not,
 * which other choice decided instead. The axis list shows a value for every
 * control whether or not it reaches anything; this is what lets it stop.
 */
export function summarizeAxisEffect(
  axes: DesignSystemAxes,
  baseline: DesignSystemAxes,
): Record<AxisName, AxisEffect> {
  return analyze(axes, baseline).axisEffect
}

function scopeOf(decisions: DesignDecision[]): ScopeSummary {
  const reachableHere = [
    ...new Set(
      decisions
        .filter((d) => d.scope === 'component' && d.component !== null)
        .map((d) => d.component as string),
    ),
  ]
  return {
    theme: { values: decisions.filter((d) => d.scope === 'theme').length },
    component: {
      reachableHere,
      declaredBySystem: Object.keys(
        (CANONICAL_THEME as { atoms?: Record<string, unknown> }).atoms ?? {},
      ).length,
    },
  }
}

/**
 * What the decisions on this screen cover: theme-wide values, and which
 * components are reachable at all.
 *
 * The two component numbers are both present because they are read from two
 * DIFFERENT producers — `reachableHere` from the paint path's own output,
 * `declaredBySystem` from `themes/default-theme.json` — not because one is
 * known to exceed the other.
 *
 * WHAT USED TO STAND HERE, AND WHY IT WENT. This comment said Studio "paints
 * from `axesToThemeV2`, whose per-component defaults cover a small subset,
 * while the canonical theme declares the full set". That was true when the
 * style table WAS the whole atom map. It is not true now: `axesToThemeV2`
 * seeds its atom map FROM the canonical theme and overlays the style table on
 * top (`axes-to-theme-v2.ts:104-109`, whose own comment records the change),
 * so nothing the platform declares is missing from the paint. Measured
 * 2026-08-26 at the editor baseline the two numbers are EQUAL — 47 and 47 —
 * and the screen has been rendering "컴포넌트 47개 · 시스템이 선언한 것은
 * 47개" while this prose asserted a gap.
 *
 * The pair still earns its place: it is the instrument that would SHOW the gap
 * reopening the day the paint path stops reading the canonical file. It is not
 * a claim that a gap exists today. `axis-resolution-list.test.tsx` pins the
 * equality, so this comment cannot drift back to the old story without that
 * test going red.
 */
export function summarizeScope(
  axes: DesignSystemAxes,
  baseline: DesignSystemAxes,
): ScopeSummary {
  return scopeOf(analyze(axes, baseline).decisions)
}

/** All three views of ONE measurement. */
export interface DesignSystemDescription {
  decisions: DesignDecision[]
  axisEffect: Record<AxisName, AxisEffect>
  scope: ScopeSummary
}

/**
 * Every view of the design system at once, from a single `analyze`.
 *
 * `measureMovers` re-resolves the whole theme once per option per axis, so
 * each of the three single-view exports above is a full sweep of the producer.
 * The screen needed two of them and now needs three — the decisions themselves
 * had no screen consumer at all until `UndecidedValuesPanel`, which is the
 * whole defect this entry point exists to make cheap to fix. Taking all three
 * off one sweep keeps the render at ONE measurement rather than three.
 *
 * The single-view exports remain for pure callers and tests that only ever
 * wanted one, and are thin wrappers over the same `analyze`, so there is no
 * second implementation to drift.
 */
export function describeDesignSystem(
  axes: DesignSystemAxes,
  baseline: DesignSystemAxes,
): DesignSystemDescription {
  const { decisions, axisEffect } = analyze(axes, baseline)
  return { decisions, axisEffect, scope: scopeOf(decisions) }
}
